import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  Divider,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Palette as PaletteIcon,
  Payment as PaymentIcon,
  PrivacyTip as PrivacyIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Translate as TranslateIcon,
  CurrencyExchange as CurrencyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirmation } from '../hooks/useConfirmation';
import ConfirmationModal from '../components/ConfirmationModal';
import { confirmations } from '../utils/confirmation';

const Settings: React.FC = () => {
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const { confirmation, showConfirmation } = useConfirmation();
  
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    orderUpdates: true,
    promotions: false,
    newsletter: true,
    
    // Security
    twoFactorAuth: false,
    loginAlerts: true,
    sessionTimeout: 30,
    
    // Preferences
    currency: 'XOF',
    autoSave: true,
  });

  const handleSettingChange = (setting: string, value: any) => {
    setSettings(prev => ({ ...prev, [setting]: value }));
    
    // Apply theme change immediately
    if (setting === 'theme') {
      toggleTheme();
      setAlert({ type: 'success', message: t.settings.themeUpdated });
    }
    
    // Apply language change immediately
    if (setting === 'language') {
      setLanguage(value);
      setAlert({ type: 'success', message: t.settings.languageUpdated });
    }

    // Show success message for other settings
    if (setting !== 'theme' && setting !== 'language') {
      setAlert({ type: 'success', message: t.settings.settingUpdated });
    }
  };

  const handleDeleteAccount = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      signOut();
    } catch (error) {
      setAlert({ type: 'error', message: t.settings.deleteAccountError });
    }
  };

  const showDeleteAccountConfirmation = () => {
    showConfirmation(
      confirmations.deleteAccount(),
      handleDeleteAccount
    );
  };

  const notificationSettings = [
    {
      title: t.settings.emailNotifications,
      description: t.settings.emailNotificationsDescription,
      icon: <EmailIcon />,
      setting: 'emailNotifications',
    },
    {
      title: t.settings.smsNotifications,
      description: t.settings.smsNotificationsDescription,
      icon: <PhoneIcon />,
      setting: 'smsNotifications',
    },
    {
      title: t.settings.pushNotifications,
      description: t.settings.pushNotificationsDescription,
      icon: <NotificationsIcon />,
      setting: 'pushNotifications',
    },
    {
      title: t.settings.orderUpdates,
      description: t.settings.orderUpdatesDescription,
      icon: <NotificationsIcon />,
      setting: 'orderUpdates',
    },
    {
      title: t.settings.promotions,
      description: t.settings.promotionsDescription,
      icon: <NotificationsIcon />,
      setting: 'promotions',
    },
    {
      title: t.settings.newsletter,
      description: t.settings.newsletterDescription,
      icon: <EmailIcon />,
      setting: 'newsletter',
    },
  ];

  const securitySettings = [
    {
      title: t.settings.twoFactorAuth,
      description: t.settings.twoFactorAuthDescription,
      icon: <SecurityIcon />,
      setting: 'twoFactorAuth',
    },
    {
      title: t.settings.loginAlerts,
      description: t.settings.loginAlertsDescription,
      icon: <SecurityIcon />,
      setting: 'loginAlerts',
    },
  ];

  const preferenceSettings = [
    {
      title: t.settings.darkMode,
      description: t.settings.darkModeDescription,
      icon: theme === 'dark' ? <DarkModeIcon /> : <LightModeIcon />,
      setting: 'theme',
      type: 'switch',
    },
    {
      title: t.settings.language,
      description: t.settings.languageDescription,
      icon: <TranslateIcon />,
      setting: 'language',
      type: 'select',
      options: [
        { value: 'fr', label: 'Français' },
        { value: 'en', label: 'English' },
      ],
    },
    {
      title: t.settings.currency,
      description: t.settings.currencyDescription,
      icon: <CurrencyIcon />,
      setting: 'currency',
      type: 'select',
      options: [
        { value: 'XOF', label: 'FCFA (XOF)' },
        { value: 'EUR', label: 'Euro (EUR)' },
        { value: 'USD', label: 'Dollar US (USD)' },
      ],
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {alert && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
        {t.settings.title}
      </Typography>

      <Grid container spacing={3}>
        {/* Notifications */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t.settings.notifications}
              </Typography>
            </Box>
            
            <List>
              {notificationSettings.map((item, index) => (
                <React.Fragment key={item.setting}>
                  <ListItem>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings[item.setting as keyof typeof settings] as boolean}
                        onChange={(e) => handleSettingChange(item.setting, e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < notificationSettings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Security */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <SecurityIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t.settings.security}
              </Typography>
            </Box>
            
            <List>
              {securitySettings.map((item, index) => (
                <React.Fragment key={item.setting}>
                  <ListItem>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={settings[item.setting as keyof typeof settings] as boolean}
                        onChange={(e) => handleSettingChange(item.setting, e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < securitySettings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Preferences */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <PaletteIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t.settings.preferences}
              </Typography>
            </Box>
            
            <List>
              {preferenceSettings.map((item, index) => (
                <React.Fragment key={item.setting}>
                  <ListItem>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.description}
                    />
                    <ListItemSecondaryAction>
                      {item.type === 'switch' ? (
                        <Switch
                          edge="end"
                          checked={item.setting === 'theme' ? theme === 'dark' : settings[item.setting as keyof typeof settings] as boolean}
                          onChange={(e) => handleSettingChange(item.setting, e.target.checked)}
                        />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={item.setting === 'language' ? language : settings[item.setting as keyof typeof settings]}
                            onChange={(e) => handleSettingChange(item.setting, e.target.value)}
                            displayEmpty
                          >
                            {item.options?.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < preferenceSettings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Account Management */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <PrivacyIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t.settings.accountManagement}
              </Typography>
            </Box>
            
            <List>
              <ListItem>
                <ListItemIcon><PrivacyIcon /></ListItemIcon>
                <ListItemText
                  primary={t.settings.personalData}
                  secondary={t.settings.personalDataDescription}
                />
                <Button variant="outlined" size="small">
                  {t.settings.manage}
                </Button>
              </ListItem>
              
              <Divider />
              
              <ListItem>
                <ListItemIcon><PaymentIcon /></ListItemIcon>
                <ListItemText
                  primary={t.settings.paymentMethods}
                  secondary={t.settings.paymentMethodsDescription}
                />
                <Button variant="outlined" size="small">
                  {t.settings.manage}
                </Button>
              </ListItem>
              
              <Divider />
              
              <ListItem>
                <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                <ListItemText
                  primary={t.settings.deleteAccount}
                  secondary={t.settings.deleteAccountDescription}
                  primaryTypographyProps={{ color: 'error' }}
                />
                <Button 
                  variant="outlined" 
                  color="error" 
                  size="small"
                  onClick={showDeleteAccountConfirmation}
                >
                  {t.settings.delete}
                </Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Modal */}
      {confirmation && (
        <ConfirmationModal
          open={confirmation.open}
          title={confirmation.title}
          message={confirmation.message}
          type={confirmation.type}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          onConfirm={confirmation.onConfirm}
          onCancel={confirmation.onCancel}
          confirmButtonColor={confirmation.confirmButtonColor}
          maxWidth={confirmation.maxWidth}
        />
      )}
    </Container>
  );
};

export default Settings; 